import { useEffect, useState } from "react";
import { auth } from "../firebase/auth";
import { signInWithPhoneNumber, RecaptchaVerifier } from "firebase/auth";
import "./LoginPage.css";

interface LoginPageProps {
  onLogin: () => void;
}

declare global {
  interface Window {
    recaptchaVerifier?: RecaptchaVerifier;
  }
}

export default function LoginPage({ onLogin }: LoginPageProps) {
  const [countryCode, setCountryCode] = useState("+47");
  const [phone, setPhone] = useState("");
  const [confirmation, setConfirmation] = useState<any>(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [cooldown, setCooldown] = useState(0);

  /* -----------------------------
     Cooldown Timer (6 seconds)
  ------------------------------*/
  useEffect(() => {
    if (cooldown <= 0) return;

    const timer = setInterval(() => {
      setCooldown((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [cooldown]);

  /* -----------------------------
     Setup Invisible reCAPTCHA
  ------------------------------*/
  const setupRecaptcha = () => {
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(
        auth,
        "recaptcha-container",
        { size: "invisible" }
      );
    }
  };

  /* -----------------------------
     Send SMS Code
  ------------------------------*/
  const handleSendCode = async () => {
    try {
      setError("");
      setLoading(true);

      if (!phone) {
        setError("Please enter your phone number.");
        setLoading(false);
        return;
      }

      setupRecaptcha();

      const fullNumber = `${countryCode}${phone}`;

      const result = await signInWithPhoneNumber(
        auth,
        fullNumber,
        window.recaptchaVerifier!
      );

      setConfirmation(result);
      setCooldown(6);
    } catch (err: any) {
      setError(err.message || "Failed to send SMS.");
    } finally {
      setLoading(false);
    }
  };

  /* -----------------------------
     Verify Code
  ------------------------------*/
  const handleVerifyCode = async () => {
    try {
      setLoading(true);
      await confirmation.confirm(code);
      onLogin();
    } catch {
      setError("Invalid verification code.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card animate-in">
        <h2 className="login-title">Wedding Login</h2>

        {error && (
          <div className="error-banner">
            {error}
            <button onClick={() => setError("")}>×</button>
          </div>
        )}

        {!confirmation ? (
          <>
            <div className="form-group">
              <label>Phone Number</label>

              <div className="phone-row">
                <select
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                  className="phone-select"
                >
                  <option value="+47">🇳🇴 Norway +47</option>
                  <option value="+45">🇩🇰 Denmark +45</option>
                  <option value="+49">🇩🇪 Germany +49</option>
                  <option value="+44">🇬🇧 United Kingdom +44</option>
                  <option value="+1">🇺🇸 United States +1</option>
                </select>

                <input
                  type="tel"
                  placeholder={
                    countryCode === "+1"
                      ? "4151234567"
                      : "12345678"
                  }
                  value={phone}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, "");
                    if (countryCode === "+1") {
                      setPhone(digits.slice(0, 10));
                    } else {
                      setPhone(digits);
                    }
                  }}
                  className="phone-input"
                />
              </div>
            </div>

            <button
              className="primary-button"
              onClick={handleSendCode}
              disabled={loading || cooldown > 0}
            >
              {loading
                ? "Sending..."
                : cooldown > 0
                ? `Wait ${cooldown}s`
                : "Send SMS Code"}
            </button>

            <div id="recaptcha-container"></div>
          </>
        ) : (
          <>
            <div className="form-group">
              <label>Verification Code</label>
              <input
                type="text"
                placeholder="6-digit code"
                value={code}
                onChange={(e) =>
                  setCode(e.target.value.replace(/\D/g, ""))
                }
                className="verification-input"
              />
            </div>

            <button
              className="primary-button"
              onClick={handleVerifyCode}
              disabled={loading}
            >
              {loading ? "Verifying..." : "Confirm"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
